/*
 * Turning notifications on.
 *
 * Two things worth knowing before reading this:
 *
 * On iOS, push works ONLY for a PWA that has been added to the home screen.
 * In Safari proper the permission prompt does not even exist, so the button
 * has to explain that rather than fail silently - which is what most web apps
 * do, and why people conclude web push "doesn't work on iPhone".
 *
 * A subscription belongs to a DEVICE, not a person, and this device may be
 * picking for several people. Dad's phone subscribes for Dad and for whichever
 * kids pick on it, which is why `subscribe` takes a list.
 */

/*
 * `Uint8Array<ArrayBuffer>`, not a bare `Uint8Array`. Since TypeScript 5.7 the
 * type is generic over its backing buffer, and only the ArrayBuffer form
 * satisfies `BufferSource` - which is what `applicationServerKey` requires.
 */
const b64ToBytes = (b64: string): Uint8Array<ArrayBuffer> => {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4)
  const raw = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'))
  const out = new Uint8Array(new ArrayBuffer(raw.length))
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i)
  return out
}

export type PushState = 'unsupported' | 'needs-install' | 'off' | 'blocked' | 'on'

/** Whether this browser can do it at all, and whether it already is. */
export async function pushState(): Promise<PushState> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    /*
     * iOS Safari has neither until the app is installed. Distinguishing the
     * two cases is the whole point: "not supported" is a dead end, "add to
     * home screen first" is an instruction.
     */
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
    const installed = window.matchMedia('(display-mode: standalone)').matches
    return iOS && !installed ? 'needs-install' : 'unsupported'
  }
  if (Notification.permission === 'denied') return 'blocked'
  const reg = await navigator.serviceWorker.getRegistration()
  const sub = await reg?.pushManager.getSubscription()
  return sub ? 'on' : 'off'
}

export async function subscribe(playerIds: string[]): Promise<PushState> {
  const r = await fetch('/api/push/key')
  const { key } = (await r.json()) as { key: string | null }
  // No VAPID key configured means push is off, not broken.
  if (!key) return 'unsupported'

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return permission === 'denied' ? 'blocked' : 'off'

  const reg = await navigator.serviceWorker.register('/sw.js')
  await navigator.serviceWorker.ready
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({
      // Required, and non-negotiable in every browser: a push that cannot
      // show a notification is not allowed.
      userVisibleOnly: true,
      applicationServerKey: b64ToBytes(key),
    }))

  const json = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } }
  await fetch('/api/push/subscribe', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      endpoint: json.endpoint,
      p256dh: json.keys?.p256dh,
      auth: json.keys?.auth,
      playerIds,
    }),
  })
  return 'on'
}

/** The endpoint this browser currently holds, if any. */
export async function currentEndpoint(): Promise<string | null> {
  try {
    const reg = await navigator.serviceWorker.getRegistration()
    const sub = await reg?.pushManager.getSubscription()
    return sub?.endpoint ?? null
  } catch {
    return null
  }
}

/** Who this device is subscribed for. A phone here carries several people. */
export async function subscribedPlayers(): Promise<string[]> {
  const endpoint = await currentEndpoint()
  if (!endpoint) return []
  const r = await fetch(`/api/push/who?endpoint=${encodeURIComponent(endpoint)}`)
  const { players } = (await r.json()) as { players: string[] }
  return players
}

/**
 * Send one notification to this device, right now.
 *
 * The only other way to learn whether push works is to wait for a real kickoff
 * and stare at a phone - and when nothing arrives, nothing tells you whether
 * the VAPID key is wrong, the browser never subscribed, or the Worker never
 * ran. This exercises the whole chain and reports the push service's own
 * status code.
 */
export async function sendTest(): Promise<{ ok: boolean; detail: string }> {
  const endpoint = await currentEndpoint()
  const r = await fetch('/api/push/test', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ endpoint }),
  })
  const body = (await r.json()) as { sent?: number; of?: number; error?: string; results?: { status: number }[] }
  if (!r.ok) return { ok: false, detail: body.error ?? 'Could not send.' }
  const codes = (body.results ?? []).map((x) => x.status).join(', ')
  return {
    ok: (body.sent ?? 0) > 0,
    detail: (body.sent ?? 0) > 0
      ? 'Sent. It should arrive in a second or two.'
      : `The push service refused it (${codes || 'no response'}).`,
  }
}

export async function unsubscribe(): Promise<PushState> {
  const reg = await navigator.serviceWorker.getRegistration()
  const sub = await reg?.pushManager.getSubscription()
  if (sub) {
    await fetch('/api/push/unsubscribe', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ endpoint: sub.endpoint }),
    })
    await sub.unsubscribe()
  }
  return 'off'
}
